import type { LogEntry } from "../state";
import { EMOJI_DISPLAY, usernameColorClass } from "../state";
import { putEvent } from "../api";
import EmojiPicker from "./EmojiPicker";
import Tooltip from "./Tooltip";
import type { AvatarBitmap } from "../avatars";
import Avatar from "./Avatar";

interface LogLineProps {
  entry: LogEntry;
  slug?: string;
  username?: string;
  reactionCounts?: Record<string, number>;
  currentUserEmojis?: string[];
  reactionsByUser?: Record<string, string | string[]>;
  usedEmojis?: string[];
  avatar?: AvatarBitmap | null;
  /** Delay in ms before showing reaction tooltip. */
  tooltipDelayMs?: number;
}

function reactorsForEmoji(
  reactionsByUser: Record<string, string | string[]> | undefined,
  emoji: string,
): string {
  if (!reactionsByUser) return "";
  return Object.entries(reactionsByUser)
    .filter(([, emojis]) => {
      const arr = Array.isArray(emojis) ? emojis : emojis ? [emojis] : [];
      return arr.includes(emoji);
    })
    .map(([u]) => u)
    .join(", ");
}

export default function LogLine({
  entry,
  slug,
  username,
  reactionCounts = {},
  currentUserEmojis = [],
  reactionsByUser = {},
  usedEmojis = [],
  avatar,
  tooltipDelayMs = 400,
}: LogLineProps) {
  const { id, type, message, username: entryUsername, created_at } = entry;
  const isChat = type === "chat_message_posted";

  const handleReaction = (emoji: string) => {
    if (!slug || !username) return;
    const hasEmoji = currentUserEmojis.includes(emoji);
    if (hasEmoji) {
      putEvent(slug, {
        type: "chat_message_reaction_removed",
        message: "",
        data: { message_id: id, username, emoji },
      });
      return true;
    } else {
      putEvent(slug, {
        type: "chat_message_reaction_added",
        message: "",
        data: { message_id: id, emoji, username },
      });
      return false;
    }
  };

  const hasReactions = Object.keys(reactionCounts).length > 0;
  const userColorClass = usernameColorClass(entryUsername);

  return (
    <div
      key={id}
      className={`text-sm p-2 pb-8 bg-orange-50 dark:bg-gray-900 border-2 relative group mb-[15px] ${
        entryUsername ? userColorClass : "border-black dark:border-gray-500"
      }`}
    >
      <div className="pr-20 font-mono text-black dark:text-gray-100">
        <div className="flex items-start gap-2 mb-0.5">
          {entryUsername ? (
            <Avatar username={entryUsername} avatar={avatar} size={64} />
          ) : null}
          <span className="block text-xs opacity-80">
            <span
              className={`font-semibold ${usernameColorClass(entryUsername)}`}
            >
              {entryUsername || "System"}
            </span>{" "}
            · {created_at}
          </span>
        </div>
        <span>{message}</span>
      </div>
      {isChat && slug && username ? (
        <div className="absolute right-2 -bottom-4 z-10 flex items-center gap-1">
          {hasReactions ? (
            <div className="flex flex-wrap gap-0.5">
              {Object.entries(reactionCounts)
                .filter(([, count]) => count > 0)
                .map(([emoji]) => {
                  const reactors = reactorsForEmoji(reactionsByUser, emoji);
                  const display = EMOJI_DISPLAY[emoji] || emoji;
                  const tooltipContent = reactors ? reactors : "No reactions";
                  return (
                    <Tooltip
                      key={emoji}
                      content={tooltipContent}
                      delayMs={tooltipDelayMs}
                    >
                      <button
                        type="button"
                        onClick={() => handleReaction(emoji)}
                        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-sm border-2 border-black dark:border-gray-100 transition-colors ${
                          currentUserEmojis.includes(emoji)
                            ? "bg-brutal-purple text-white"
                            : "bg-white dark:bg-gray-950 hover:bg-orange-100 dark:hover:bg-gray-800"
                        }`}
                      >
                        {display} {reactionCounts[emoji] || 0}
                      </button>
                    </Tooltip>
                  );
                })}
            </div>
          ) : null}
          <EmojiPicker
            onSelect={handleReaction}
            usedEmojis={usedEmojis}
            trigger="+"
            triggerClassName="brutal-btn inline-flex items-center justify-center min-h-[1.5rem] min-w-[1.5rem] p-0 text-sm font-bold bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-300 border-2 border-black dark:border-gray-100"
          />
        </div>
      ) : null}
    </div>
  );
}
